/**
 * tokenStorage.js — camada de armazenamento seguro dos tokens OIDC.
 *
 * Estratégia OWASP recomendada para SPA:
 *   - access_token  → memória (closure), nunca em localStorage/sessionStorage.
 *   - refresh_token → sessionStorage (escopo de aba; limpo ao fechar).
 *   - Sem cookies HttpOnly nesta fase (requer backend BFF — Fase 4).
 *
 * A camada é substituível: altere apenas este módulo para migrar
 * para cookie HttpOnly + BFF sem tocar no resto do auth.
 *
 * NUNCA exponha tokens em URL, logs ou window.location.
 */

// Access token fica em memória — nunca serializado.
let _accessToken = null;

const REFRESH_KEY  = 'nava_rt';
const ID_TOKEN_KEY = 'nava_id';

// ─── Access token (memória) ──────────────────────────────────────────────────

export function setAccessToken(token) {
  _accessToken = token;
}

export function getAccessToken() {
  return _accessToken;
}

export function clearAccessToken() {
  _accessToken = null;
}

// ─── Refresh token (sessionStorage) ─────────────────────────────────────────

export function setRefreshToken(token) {
  try {
    sessionStorage.setItem(REFRESH_KEY, token);
  } catch {
    // sessionStorage indisponível (modo privado estrito) — opera sem refresh
  }
}

export function getRefreshToken() {
  try {
    return sessionStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

export function clearRefreshToken() {
  try {
    sessionStorage.removeItem(REFRESH_KEY);
  } catch {
    // noop
  }
}

// ─── ID token (sessionStorage — para claims de UI) ──────────────────────────

export function setIdToken(token) {
  try {
    sessionStorage.setItem(ID_TOKEN_KEY, token);
  } catch {
    // noop
  }
}

export function getIdToken() {
  try {
    return sessionStorage.getItem(ID_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function clearIdToken() {
  try {
    sessionStorage.removeItem(ID_TOKEN_KEY);
  } catch {
    // noop
  }
}

// ─── Limpar tudo (logout) ────────────────────────────────────────────────────

export function clearAllTokens() {
  clearAccessToken();
  clearRefreshToken();
  clearIdToken();
}

// ─── Parse seguro de JWT (apenas payload, sem verificação de assinatura) ─────
// A verificação real de assinatura acontece no backend (FastAPI).

/**
 * Decodifica o payload de um JWT sem verificar assinatura.
 * Usado apenas para extrair claims de exibição (name, matrix_id, etc.).
 * NÃO confiar para decisões de segurança — a autorização é feita via RLS + backend.
 *
 * @param {string} token
 * @returns {Record<string, unknown> | null}
 */
export function decodeJwtPayload(token) {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    // base64url → base64
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '='));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Verifica se um token JWT está expirado (com 30s de margem).
 *
 * @param {string} token
 * @returns {boolean}
 */
export function isTokenExpired(token) {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') return true;
  // 30 segundos de margem para refresh preventivo
  return Date.now() / 1000 >= payload.exp - 30;
}
